import appStore from '@stores/appStore'
import { AssistantChatMessage } from '@stores/assistantStore'
import { observer } from 'mobx-react'
import { useRef } from 'react'

import AnswerNotices, {
  hasNotice,
  NOTICE_ANSWER_CUT_SHORT,
} from './AnswerNotices'
import { buildCopyPayload } from './assistantCopy'
import { renderAssistantDates } from './assistantDates'
import AssistantMarkdown from './assistantMarkdown'
import { assistantZoneLabel } from './assistantZoneLabel'
import CitationChips from './CitationChips'
import { splitCitations } from './citationLinks'
import { canOpenCitation, openCitation } from './citationNavigation'
import ClarificationPrompt from './ClarificationPrompt'
import CopyAnswerButton from './CopyAnswerButton'
import FeedbackButtons from './FeedbackButtons'
import MessageParts from './parts'
import AppointmentHistoryPart, {
  APPOINTMENT_HISTORY_PART,
  appointmentHistoryText,
} from './parts/AppointmentHistoryPart'
import SessionTablePart, {
  SESSION_TABLE_PART,
  sessionTableText,
} from './parts/SessionTablePart'
import {
  markRecordTokens,
  recordTokenIds,
  recordTokensAsText,
} from './recordTokens'
import { labelSessionCitations, SESSION_CITATION } from './sessionCitations'
import { AssistantAvatar, KW } from './ui'

interface Props {
  message: AssistantChatMessage
  streaming?: boolean
}

const MessageBubble = ({ message, streaming }: Props) => {
  const markdownRef = useRef<HTMLDivElement>(null)

  if (message.role === 'User') {
    return (
      <div className="kw-up flex flex-col items-end">
        <div
          className="max-w-[82%] whitespace-pre-wrap px-[13px] py-[9px] text-sm leading-[1.45]"
          style={{
            background: '#f1f3f8',
            color: KW.ink,
            border: '1px solid #e3e8ef',
            borderRadius: '14px 14px 4px 14px',
          }}
        >
          {message.content}
        </div>
        {message.excludedFromContext && (
          <div className="mt-1 text-xs italic" style={{ color: KW.muted }}>
            Not sent to the assistant (blocked by the content filter)
          </div>
        )}
      </div>
    )
  }

  // An answer can be copied once it has ended, finished or cut short (which has no id). A
  // clarifying question is not an answer.
  const ended = !streaming && !message.clarification
  const canCopy = ended && !!message.content.trim()
  const canRate = ended && !!message.id

  const dateOptions = {
    dateFormat: appStore.currentUser?.dateFormat,
    defaultTimeZone: appStore.timezone,
    zoneLabel: assistantZoneLabel,
  }
  // The records the answer names by ref are shown where it writes them, by the server's ids.
  const renderedContent = markRecordTokens(
    renderAssistantDates(message.content, {
      ...dateOptions,
      streaming,
    }),
    message.citations
  )
  const referenced = recordTokenIds(message.content)

  // A record the text names is a link there; only the others are listed under the answer. The
  // labels are looked for in the text as shown: a session is named by its start, shown as a date.
  // An answer that names records by ref is linked by those refs alone: no other name in its text
  // is looked for (answers written before refs still are). A session is named by its start.
  const labelled = labelSessionCitations(
    message.content,
    message.citations,
    dateOptions
  )
  const byText = referenced.size
    ? labelled.filter(c => c.type === SESSION_CITATION)
    : labelled
  const {
    linked,
    named,
    rest: unnamedByText,
  } = splitCitations(renderedContent, byText, canOpenCitation)
  const unnamed = referenced.size
    ? [
        ...unnamedByText,
        ...labelled.filter(
          c => c.type !== SESSION_CITATION && c.openable !== false
        ),
      ]
    : unnamedByText
  // A session is never a pill: its label is a time, which says nothing without the row it is in.
  // Nor is a record the text names by ref: it is a link where the text names it.
  const rest = unnamed.filter(
    c =>
      c.type !== SESSION_CITATION && !referenced.has(String(c.id).toLowerCase())
  )

  // The sessions a calendar search listed: a table drawn from the server's rows, under the text
  // and inside its block, so Copy takes it with the answer.
  const sessionTables = (message.parts ?? []).filter(
    p => p.type === SESSION_TABLE_PART
  )
  // A case's appointment history: the case screen's table, drawn from the server's rows.
  const appointmentHistories = (message.parts ?? []).filter(
    p => p.type === APPOINTMENT_HISTORY_PART
  )

  const copyPayload = () =>
    buildCopyPayload(
      [
        recordTokensAsText(message.content, message.citations),
        ...sessionTables.map(p => sessionTableText(p, dateOptions)),
        ...appointmentHistories.map(p => appointmentHistoryText(p)),
      ].join('\n\n'),
      {
        ...dateOptions,
        citations: rest,
        rendered: markdownRef.current,
      }
    )

  return (
    <div className="kw-up flex items-start gap-[10px]">
      <AssistantAvatar size={28} />
      <div className="min-w-0 flex-1 pt-px">
        <div className="kw-md" ref={markdownRef}>
          {/* Model text is only ever rendered by AssistantMarkdown (no links, images or HTML). */}
          <AssistantMarkdown
            citations={linked}
            onOpenCitation={openCitation}
            namedOnly={named}
            records={message.citations}
            canOpenCitation={canOpenCitation}
          >
            {renderedContent}
          </AssistantMarkdown>
          {sessionTables.map((part, index) => (
            <SessionTablePart key={`sessions-${index}`} part={part} />
          ))}
          {appointmentHistories.map((part, index) => (
            <AppointmentHistoryPart key={`appointments-${index}`} part={part} />
          ))}
          {streaming && <span className="animate-pulse">▍</span>}
        </div>

        {/* An answer cut at the length limit says so in its own notice. */}
        {message.partial &&
          !hasNotice(message.parts, NOTICE_ANSWER_CUT_SHORT) && (
            <div className="mt-1 text-xs italic" style={{ color: KW.muted }}>
              (incomplete answer)
            </div>
          )}

        {message.clarification && <ClarificationPrompt message={message} />}

        {!streaming && <AnswerNotices parts={message.parts} />}

        {!streaming && rest.length > 0 && <CitationChips citations={rest} />}

        <MessageParts parts={message.parts} />

        {(canCopy || canRate) && (
          // Wraps: the form of a bad rating opens on a line of its own under the buttons.
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {canCopy && <CopyAnswerButton getPayload={copyPayload} />}
            {canRate && <FeedbackButtons message={message} />}
          </div>
        )}
      </div>
    </div>
  )
}

export default observer(MessageBubble)
